import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CompanyStory = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  const values = [
    {
      icon: 'Heart',
      title: 'Customer First',
      description: 'Every decision we make puts our customers at the center, ensuring exceptional service and satisfaction.'
    },
    {
      icon: 'Leaf',
      title: 'Eco-Friendly',
      description: 'We use environmentally safe detergents and processes to protect both your clothes and our planet.'
    },
    {
      icon: 'Shield',
      title: 'Quality Assured',
      description: 'Rigorous quality checks and premium care standards ensure your garments receive the best treatment.'
    },
    {
      icon: 'Clock',
      title: 'Reliable Service',
      description: 'Punctual pickup, timely delivery, and consistent quality you can depend on every single time.'
    }
  ];

  return (
    <section ref={ref} className="py-20 bg-primary relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-40 h-40 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-32 h-32 bg-accent rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Section Header */}
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="inline-flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium"
              >
                <Icon name="Star" size={16} />
                <span>Our Story</span>
              </motion.div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Crafting Excellence in
                <span className="block text-accent">Laundry Care</span>
              </h2>
            </div>

            {/* Story Content */}
            <div className="space-y-6">
              <p className="text-lg text-white/90 leading-relaxed text-justify">
                Founded in 2019 with a simple mission: to provide Coimbatore families with premium laundry services that save time without compromising on quality. What started as a small neighborhood service has grown into the city's most trusted laundry partner.
              </p>
              
              <p className="text-white/80 leading-relaxed text-justify">
                Our journey began when we noticed busy professionals and families struggling to maintain their clothing while managing hectic schedules. We saw an opportunity to bridge this gap with technology-driven solutions, eco-friendly practices, and unwavering commitment to customer satisfaction.
              </p>

              <div className="bg-white/10 rounded-xl p-6 border border-white/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Target" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Our Mission</h3>
                    <p className="text-white/80 text-justify">
                      To revolutionize laundry care in Coimbatore by combining traditional expertise with modern convenience, ensuring every garment receives premium treatment while giving our customers more time for what matters most.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Achievement Stats */}
            <div className="grid grid-cols-3 gap-6 pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-accent mb-1">5+</div>
                <div className="text-sm text-white/80">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent mb-1">15K+</div>
                <div className="text-sm text-white/80">Happy Customers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent mb-1">25+</div>
                <div className="text-sm text-white/80">Service Points</div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Parallax Image */}
          <motion.div
            style={{ y, opacity }}
            className="relative"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-elevation-3">
                <Image
                  src="https://images.unsplash.com/photo-1582735689369-4fe89db7114c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Professional laundry facility with modern equipment and clean environment"
                  className="w-full h-[500px] object-cover"
                />
              </div>

              {/* Floating Quality Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="absolute top-4 left-4 sm:-top-6 sm:-left-6 bg-success rounded-xl p-3 sm:p-4 shadow-elevation-2 border border-border max-w-xs z-20"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-white">ISO Certified</p>
                    <p className="text-xs text-white/80">Quality Management System</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Delivery Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="absolute bottom-4 right-4 sm:-bottom-6 sm:-right-6 bg-accent rounded-xl p-3 sm:p-4 shadow-elevation-2 border border-border max-w-xs z-20"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Icon name="Star" size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-white">4.9/5</p>
                    <p className="text-xs text-white/80">Customer Rating</p>
                  </div>
                </div>
              </motion.div>

            </div>
          </motion.div>
        </div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Our Core Values
            </h3>
            <p className="text-white/80 max-w-2xl mx-auto">
              The principles that guide everything we do and shape every interaction with our customers
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values?.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-colors duration-300">
                  <Icon name={value?.icon} size={28} className="text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-accent transition-colors duration-300">
                  {value?.title}
                </h4>
                <p className="text-sm text-white/80 leading-relaxed">
                  {value?.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CompanyStory;